// ── Kanban board page ──

import { api } from '../api.js';
import { getUser } from '../auth.js';
import { showToast } from '../ui.js';
import { escapeHtml, renderAvatarHtml, renderAgentBadge, renderDuePill, canStartTask } from '../utils.js';
import { openTaskDetail, closeTaskDetail, restoreBoardFocus } from '../components/task-detail.js';
import { showAssigneePicker, removeAssigneePicker } from '../components/assignee-picker.js';
import { loadStatuses, statusLabel, roleStatus, invalidateStatuses, isClosedStatus, closedNames } from '../lib/statuses.js';
import { findInsertBefore, dropIndex } from '../lib/drag-order.js';
import { bindHoverExpand, collapseCard } from '../lib/hover-expand.js';
import { renderPermissionModeBadge } from '../lib/agent-permission-mode.js';
import { renderCardDescription } from '../lib/board-card-description.js';
import { renderUrgencyBadge } from '../lib/task-urgency.js';
import { renderDeviceAttribution } from '../lib/device-attribution.js';
import { bindCardSelection, cardCheckboxHtml, groupCheckboxHtml, groupCheckState } from '../lib/card-selection.js';

const DRAG_THRESHOLD = 5;
// Grab-to-pan / edge auto-scroll (TPT113).
const EDGE_ZONE = 64; // px from the board's own edge that arms auto-scroll
const EDGE_MAX_SPEED = 20; // px/frame at the very edge, ramped down across the zone
const INTERACTIVE_SEL = 'a, button, input, textarea, select, [contenteditable]';

let state = {
  projectId: null,
  tasks: [],
  members: [],
  statuses: [],
  drag: null,
  // Edit Board (C1183) — column-header drag reorder, threaded from board.js's toolbar
  // toggle. Card drag-and-drop (existing) stays live regardless of this flag.
  editBoardMode: false,
  onStatusesChanged: null,
  onAddTask: null,
  onDataChanged: null, // bubbles "server data changed" up to board.js (C1261) — see reloadTasks()
  sprintRows: null, // board.js's project-wide `{ priority }` rows for the drawer's Sprint select (TPT628)
  canWrite: true, // false (no task:write) → no add buttons, card drag or assignee picker
  // (TPT9099) board.js's createBulkSelection(), writers only — null draws no checkboxes. It
  // outlives this view (board.js re-mounts it on every reload); cardSelect is this mount's
  // bindCardSelection() over it.
  selection: null,
  cardSelect: null,
  colDrag: null,
  pan: null, // TPT113 grab-to-pan
  // Owner tokens of the drawer and assignee picker this view opened (TPT647), passed to their
  // closers on teardown so a late teardown leaves another page's drawer/picker open.
  detailToken: null,
  pickerToken: null,
};

function resetState() {
  state = {
    projectId: null,
    tasks: [],
    members: [],
    statuses: [],
    drag: null,
    editBoardMode: false,
    onStatusesChanged: null,
    onAddTask: null,
    onDataChanged: null,
    sprintRows: null,
    canWrite: true,
    selection: null,
    cardSelect: null,
    colDrag: null,
    pan: null,
    detailToken: null,
    pickerToken: null,
  };
}

// Free board space = not a card, not a native control. Shared by grab-to-pan and the
// Edit Board column drag (TPT113) so the two gestures never disagree about what counts
// as "empty" — `.kanban-column-drag-handle` is excluded by callers individually, since
// it means different things in each mode (only a handle in normal mode; either handle
// or free column space starts a column drag in Edit Board mode).
function isFreeBoardSpace(target) {
  return !target.closest('.kanban-card') && !target.closest(INTERACTIVE_SEL);
}

// Bubble a data-changing event (drawer close, drag-save failure, quick-create) up to
// board.js's own reload rather than refetching into state.tasks here — state.tasks IS
// board.js's `filtered` array (applyFilters output), a throwaway once any filter is active
// (C1261). Never fetches here: the rows always come from board.js's filtered load. With no
// owner callback — only after teardown, e.g. a drawer onClose landing ~220ms late — it no-ops.
async function reloadTasks() {
  if (!state.onDataChanged) return;
  await state.onDataChanged();
}

// Columns follow this project's Workflow tab order (display_order ASC) — one column
// per status, colored by that status's own `color` token (C1182). `loadStatuses()`
// already returns display_order-sorted rows (falling back to the legacy 5 on error), so
// this just maps them; no separate fetch/sort here. `id` carries through for the
// Edit Board (C1183) column-drag reorder payload (PUT /statuses/reorder wants status ids).
function getColumns() {
  return state.statuses.map(s => ({
    id: s.id,
    key: s.name,
    label: statusLabel(s.name),
    dot: s.color ? `var(--c-${s.color})` : 'var(--c-overlay1)',
  }));
}

// ── Rendering ──

function renderKanbanCard(task) {
  const catClass = task.category === 'CODING' ? 'badge-coding' : 'badge-human';
  const tags = (task.tags || []).slice(0, 3);
  const extraTags = (task.tags || []).length - 3;
  const checked = !!state.selection && state.selection.has(task.task_key);

  let html = `<div class="kanban-card task-card${checked ? ' card-bulk-selected' : ''}" data-task-key="${escapeHtml(task.task_key)}">`;
  html += `<a class="badge kanban-card-key ${catClass} task-key-link" href="#/projects/${state.projectId}/tasks/${encodeURIComponent(task.task_key)}" draggable="false" title="Open task page">${escapeHtml(task.task_key)}</a>`;
  // (TPT9099) The hover checkbox sits on the key's line, which holds nothing else — hidden, it
  // still keeps its slot, so revealing it moves nothing.
  if (state.selection) html += cardCheckboxHtml(task.task_key, checked);
  html += `<div class="kanban-card-title">${escapeHtml(task.title)}</div>`;
  // (TPT376) Description, hidden at rest (.kanban-card-description) and revealed by the
  // hover-expand overlay (.task-card-expanded) — kanban stays title-only until hovered.
  // (TPT465) Rendered as sandboxed Markdown — raw HTML stays inert text and only
  // http(s)/relative links survive; .card-md keeps the typography compact. (TPT620) A board
  // row's description is a 600-character prefix; a cut one (description_truncated) ends with a
  // "…" marker pointing at the full task. Both come from lib/board-card-description.js.
  const desc = renderCardDescription(task);
  if (desc) html += `<div class="kanban-card-description card-md text-muted">${desc}</div>`;
  html += renderUrgencyBadge(task, state.language, escapeHtml);
  html += renderDuePill(task, closedNames(state.statuses));
  html += renderDeviceAttribution(task, state.language);
  const assigneeUser = task.assignee ? state.members.find(m => (m.user_id || m.id) === task.assignee) : null;
  html += `<div style="display:flex;align-items:flex-end;gap:0.5rem;">`;
  if (tags.length) {
    html += `<div class="kanban-card-tags" style="flex:1;min-width:0;">`;
    html += tags.map(t => `<span class="tag">${escapeHtml(t)}</span>`).join('');
    if (extraTags > 0) html += `<span class="text-faint" style="font-size:0.7rem"> +${extraTags}</span>`;
    html += `</div>`;
  } else {
    html += `<div style="flex:1"></div>`;
  }
  html += renderPermissionModeBadge(task.agent_permission_mode, state.projectPermissionMode);
  html += `<div class="card-avatar-wrap" data-task-key="${escapeHtml(task.task_key)}" data-action="assign">`;
  html += renderAvatarHtml(assigneeUser, 24);
  html += renderAgentBadge(task, state.members, { overlap: true });
  html += `</div></div>`;
  html += `</div>`;
  return html;
}

function renderColumn(col, tasks) {
  let html = `<div class="kanban-column" data-status-id="${col.id}">`;
  html += `<div class="kanban-column-header${state.editBoardMode ? ' kanban-column-header--editable' : ''}"${state.editBoardMode ? ' title="Drag to reorder"' : ''}>`;
  if (state.editBoardMode) html += `<span class="kanban-column-drag-handle">&#10495;</span>`;
  html += `<span class="kanban-column-dot" style="background:${col.dot}"></span>`;
  html += `<span>${col.label}</span>`;
  html += `<span class="kanban-column-count">${tasks.length}</span>`;
  // (TPT9099) "Select all" for the column, after the count — nothing to select in an empty one.
  if (state.selection && tasks.length) {
    html += groupCheckboxHtml(col.key, col.label, groupCheckState(tasks.map(t => t.task_key), state.selection));
  }
  html += `</div>`;
  html += `<div class="kanban-column-body" data-status="${col.key}">`;
  html += tasks.map(renderKanbanCard).join('');
  if (state.canWrite) html += `<button type="button" class="quick-create-trigger kanban-add-item" data-status="${escapeHtml(col.key)}">+ Add item</button>`;
  html += `</div></div>`;
  return html;
}

function renderKanban() {
  const board = document.getElementById('kanban-board');
  if (!board) return;

  const columns = getColumns();
  const grouped = new Map();
  for (const col of columns) grouped.set(col.key, []);
  for (const t of state.tasks) {
    const arr = grouped.get(t.status);
    if (arr) arr.push(t);
  }
  // (TPT9099) The selection never holds a card this paint leaves out — one whose status has no
  // column, or one a reload dropped — so the toolbar's count is always the visible ticks.
  if (state.selection) {
    const rendered = new Set();
    for (const arr of grouped.values()) for (const t of arr) rendered.add(t.task_key);
    state.selection.retain(key => rendered.has(key));
  }

  // Always render every status column — even when the current sprint selection
  // matches zero tasks — so every status stays a valid drop target.
  // (TPT112) Preserve horizontal scroll position across re-renders — innerHTML
  // resets scrollLeft to 0, which would snap the user back to the leftmost column
  // on every status drag, quick-create, and reload once horizontal scroll is the
  // primary way to reach far columns.
  const scrollLeft = board.scrollLeft;
  board.innerHTML = columns.map(col => renderColumn(col, grouped.get(col.key) || [])).join('');
  board.scrollLeft = scrollLeft;
  // (TPT378) innerHTML above discarded the focused card element; re-mark its replacement.
  restoreBoardFocus(board);
  if (state.cardSelect) state.cardSelect.sync();
  // (TPT112) CSS hook — styles.css uses [data-columns="1|2|3"] to widen columns to
  // fill available width on a project with few statuses, instead of a dead gutter.
  board.dataset.columns = columns.length;
  // (TPT9189) A column drag pressed or under way still holds a column this rewrite replaced.
  syncColumnDrag();
}

// ── Edge auto-scroll (TPT113) ──
// Shared by card drag and Edit Board column drag — a drag held near the board's left/right
// edge scrolls the board under the pointer so an offscreen column becomes a valid drop
// target without releasing. rAF-driven rather than mousemove-driven: a pointer held still
// inside the edge zone must keep scrolling. Because the board's DOM moves under a
// stationary cursor, each tick re-invokes the caller's own drop-target resolution at the
// last known pointer position — otherwise the placeholder (and the drop target read on
// mouseup) goes stale mid-scroll.

let edgeScrollRaf = null;
let edgeScrollVel = 0;
let edgeScrollBoard = null;
let edgeScrollOnTick = null;

function edgeScrollTick() {
  if (!edgeScrollBoard || edgeScrollVel === 0) {
    edgeScrollRaf = null;
    return;
  }
  edgeScrollBoard.scrollLeft += edgeScrollVel; // scrollLeft self-clamps at both ends
  if (edgeScrollOnTick) edgeScrollOnTick();
  edgeScrollRaf = requestAnimationFrame(edgeScrollTick);
}

// Call on every drag mousemove with the pointer's clientX and a callback that re-resolves
// the caller's drop target at the drag's last known pointer position.
function updateEdgeScroll(board, clientX, onTick) {
  edgeScrollBoard = board;
  edgeScrollOnTick = onTick;
  const rect = board.getBoundingClientRect();
  let vel = 0;
  if (clientX < rect.left + EDGE_ZONE) {
    const depth = Math.max(0, rect.left + EDGE_ZONE - clientX);
    vel = -EDGE_MAX_SPEED * Math.min(1, depth / EDGE_ZONE);
  } else if (clientX > rect.right - EDGE_ZONE) {
    const depth = Math.max(0, clientX - (rect.right - EDGE_ZONE));
    vel = EDGE_MAX_SPEED * Math.min(1, depth / EDGE_ZONE);
  }
  edgeScrollVel = vel;
  if (vel !== 0 && edgeScrollRaf == null) edgeScrollRaf = requestAnimationFrame(edgeScrollTick);
}

function stopEdgeScroll() {
  if (edgeScrollRaf != null) cancelAnimationFrame(edgeScrollRaf);
  edgeScrollRaf = null;
  edgeScrollVel = 0;
  edgeScrollBoard = null;
  edgeScrollOnTick = null;
}

// ── Drag and drop ──

let onMouseDown, onMouseMove, onMouseUp;

// Resolve and highlight the column-body drop target + placeholder position for the
// active card drag at the given viewport point. Extracted from onMouseMove (TPT113) so
// the edge-auto-scroll rAF tick can re-run it at the drag's last known pointer position
// while the board scrolls under a stationary cursor.
function updateCardDropTarget(clientX, clientY) {
  const d = state.drag;
  if (!d || !d.placeholder) return;

  const target = document.elementFromPoint(clientX, clientY);
  if (!target) return;
  const colBody = target.closest('.kanban-column-body');

  // Clear all drag-over highlights
  document.querySelectorAll('.kanban-column-body.drag-over').forEach(el => el.classList.remove('drag-over'));

  if (!colBody) return;
  colBody.classList.add('drag-over');

  // Find insertion point within column
  const cards = [...colBody.querySelectorAll('.kanban-card:not(.kanban-card-hidden)')];
  const insertBefore = findInsertBefore(cards, clientX, clientY, 'y');

  // Move placeholder
  if (insertBefore) {
    colBody.insertBefore(d.placeholder, insertBefore);
  } else {
    colBody.appendChild(d.placeholder);
  }
}

function bindDragAndDrop() {
  onMouseDown = (e) => {
    const card = e.target.closest('.kanban-card');
    if (!card) return;
    // A press on a control — the key link, the selection checkbox (TPT9099) — is that control's.
    if (e.target.closest('select, button, a, input, textarea, [contenteditable]')) return;

    // (TPT9099) Cmd/Ctrl/Shift-click selects the card instead: no drag, picker or drawer.
    if (state.cardSelect && state.cardSelect.gesture(e, card)) return;

    // Avatar click → assignee picker, no drag
    const avatarWrap = e.target.closest('.card-avatar-wrap');
    if (avatarWrap && state.canWrite) {
      e.preventDefault();
      const taskKey = avatarWrap.dataset.taskKey;
      state.pickerToken = showAssigneePicker(avatarWrap, {
        members: state.members,
        currentAssignee: state.tasks.find(t => t.task_key === taskKey)?.assignee,
        onSelect: (userId) => handleAssign(taskKey, userId),
      });
      return;
    }

    const rect = card.getBoundingClientRect();
    state.drag = {
      taskKey: card.dataset.taskKey,
      el: card,
      clone: null,
      placeholder: null,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    e.preventDefault();
  };

  onMouseMove = (e) => {
    const d = state.drag;
    if (!d) return;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    // Wait for threshold before starting visual drag
    if (!d.moved) {
      // Without task:write a press never becomes a drag — it stays a click, which still
      // opens the task.
      if (!state.canWrite) return;
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
      d.moved = true;

      // (TPT376) The card is normally hover-expanded by the time it's grabbed (position:fixed
      // overlay, un-clamped content, z-index above the clone's). Collapse it first so the
      // clone, its width and the drop placeholder are all built from the resting card — same
      // order as the Task App's _startDrag() — and re-anchor the grab offset to that rect.
      if (collapseCard(d.el)) {
        const restRect = d.el.getBoundingClientRect();
        d.offsetX = Math.min(Math.max(d.startX - restRect.left, 0), restRect.width);
        d.offsetY = Math.min(Math.max(d.startY - restRect.top, 0), restRect.height);
      }

      // Create floating clone
      d.clone = d.el.cloneNode(true);
      d.clone.classList.add('kanban-card-dragging');
      d.clone.style.width = d.el.offsetWidth + 'px';
      document.body.appendChild(d.clone);

      // Create placeholder
      d.placeholder = document.createElement('div');
      d.placeholder.className = 'kanban-card-placeholder';
      d.placeholder.style.height = d.el.offsetHeight + 'px';
      d.el.parentNode.insertBefore(d.placeholder, d.el);

      // Hide original
      d.el.classList.add('kanban-card-hidden');
    }

    // Position clone
    d.clone.style.left = (e.clientX - d.offsetX) + 'px';
    d.clone.style.top = (e.clientY - d.offsetY) + 'px';

    d.lastX = e.clientX;
    d.lastY = e.clientY;
    updateCardDropTarget(e.clientX, e.clientY);

    // TPT113 — auto-scroll the board when the dragged clone nears its left/right edge.
    // Re-resolves the drop target on every scroll tick since the DOM moves under a
    // pointer that may not itself be moving.
    const board = document.getElementById('kanban-board');
    if (board) updateEdgeScroll(board, e.clientX, () => updateCardDropTarget(d.lastX, d.lastY));
  };

  onMouseUp = async (e) => {
    const d = state.drag;
    if (!d) return;
    stopEdgeScroll();

    // Clear highlights
    document.querySelectorAll('.kanban-column-body.drag-over').forEach(el => el.classList.remove('drag-over'));

    if (!d.moved) {
      // Was a click, not a drag — open detail panel
      const clickedKey = d.taskKey;
      state.drag = null;
      // (TPT378) The drawer keeps the clicked card focused (TPT377 ring) and blurs the rest
      // of the board until it closes — it sets and lifts that itself from focusCard/focusBoard,
      // and renderKanban() re-applies it after a repaint via restoreBoardFocus().
      state.detailToken = openTaskDetail(state.projectId, clickedKey, {
        allTasks: state.tasks,
        sprintRows: state.sprintRows,
        focusCard: d.el,
        focusBoard: document.getElementById('kanban-board'),
        onClose: () => reloadTasks(),
      });
      return;
    }

    // Determine target status
    const colBody = d.placeholder.closest('.kanban-column-body');
    const targetStatus = colBody ? colBody.dataset.status : null;

    // Cleanup DOM
    if (d.clone) d.clone.remove();
    if (d.placeholder) d.placeholder.remove();
    d.el.classList.remove('kanban-card-hidden');

    const task = state.tasks.find(t => t.task_key === d.taskKey);
    state.drag = null;

    if (!task || !targetStatus || task.status === targetStatus) return;

    // Block moving another member's task onto the in-progress-role status
    const inProgressName = roleStatus(state.statuses, 'in_progress')?.name;
    if (targetStatus === inProgressName && !canStartTask(task, getUser())) {
      showToast('Cannot start a task assigned to another member', 'error');
      return;
    }

    // Optimistic update
    task.status = targetStatus;
    renderKanban();

    try {
      await api.patch('/projects/' + state.projectId + '/tasks/' + task.task_key, { status: targetStatus });
    } catch (err) {
      showToast('Failed to update status', 'error');
      // Server is the truth now — re-derive everything through the owner rather than
      // splicing an unfiltered refetch into this (possibly filtered) array (C1261).
      await reloadTasks();
    }
  };

  document.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}

async function handleAssign(taskKey, userId) {
  const task = state.tasks.find(t => t.task_key === taskKey);
  if (!task) return;
  const old = task.assignee;
  task.assignee = userId;
  renderKanban();
  try {
    await api.patch('/projects/' + state.projectId + '/tasks/' + taskKey, { assignee: userId });
  } catch (err) {
    task.assignee = old;
    renderKanban();
    showToast('Failed to assign', 'error');
  }
}

function cleanupDrag() {
  stopEdgeScroll();
  if (state.drag) {
    if (state.drag.clone) state.drag.clone.remove();
    if (state.drag.placeholder) state.drag.placeholder.remove();
    if (state.drag.el) state.drag.el.classList.remove('kanban-card-hidden');
    state.drag = null;
  }
  document.querySelectorAll('.kanban-column-body.drag-over').forEach(el => el.classList.remove('drag-over'));
  if (onMouseDown) document.removeEventListener('mousedown', onMouseDown);
  if (onMouseMove) document.removeEventListener('mousemove', onMouseMove);
  if (onMouseUp) document.removeEventListener('mouseup', onMouseUp);
}

// ── Column-header drag reorder (Edit Board, C1183) ──
// Same house pointer-drag idiom as bindDragAndDrop() above and settings.js's Workflow
// tab row-drag (C1182) — clone + placeholder + 5px threshold, no HTML5 DnD. Horizontal
// instead of vertical: reorders `.kanban-column` siblings left-to-right instead of
// `.kanban-card`/`.wf-row` top-to-bottom. Listeners are only attached while
// `state.editBoardMode` is true (bindColumnDrag() call is itself conditional), so
// dragging a column header does nothing outside Edit Board mode.

let onColMouseDown, onColMouseMove, onColMouseUp;

// Resolve the column insertion point for the active column drag at the given viewport
// point. Extracted from onColMouseMove (TPT113) so the edge-auto-scroll rAF tick can
// re-run it while the board scrolls under a stationary cursor.
function updateColumnDropTarget(clientX, clientY) {
  const d = state.colDrag;
  if (!d || !d.placeholder) return;
  if (!syncColumnDrag()) return;
  const board = document.getElementById('kanban-board');
  if (!board) return;
  const cols = [...board.querySelectorAll('.kanban-column:not(.kanban-column-hidden)')];
  const insertBefore = findInsertBefore(cols, clientX, clientY, 'x');
  if (insertBefore) board.insertBefore(d.placeholder, insertBefore);
  else board.appendChild(d.placeholder);
}

// The column a column drag drops status `statusId` at: how many other status columns sit in front
// of the placeholder on `board`. The dragged column stays on the board while the drag runs, only
// hidden, so a raw child index counted it and saved every rightward drop one slot too far right
// (C1394) — hence `.kanban-column-hidden`, and why this must run before the drop's cleanup un-hides
// it. A re-render during the drag (the previous drop's PUT landing ends in renderKanban()) leaves an
// unhidden copy of the dragged column on the board, which mousemove moves the placeholder in beside,
// so columns are skipped by status id as well (TPT9185). -1 when the placeholder is not on `board`
// (the board was re-rendered after the last mousemove): nothing is saved.
export function columnDropIndex(board, placeholder, statusId) {
  if (!board || !placeholder || !board.contains(placeholder)) return -1;
  return dropIndex(board, placeholder, {
    selector: '.kanban-column',
    hiddenClass: 'kanban-column-hidden',
    filter: (el) => el.dataset.statusId !== String(statusId),
  });
}

// Points column drag `d` at its column as `board` has it now (TPT9189). renderKanban() replaces every
// column, so a re-render while the drag is pressed or under way (the previous drop's PUT answering
// ends in one) leaves `d.el` off the board: it measures 0 by 0, has no parent to put the placeholder
// beside, and its re-rendered copy shows next to the clone. `d.el` becomes that copy, found by status
// id, hidden like the original once the drag is under way. False when no column on `board` carries
// the status any more.
export function relinkColumnDrag(board, d) {
  if (d.el.isConnected) return true;
  const col = board ? [...board.querySelectorAll('.kanban-column')].find(el => el.dataset.statusId === String(d.statusId)) : null;
  if (!col) return false;
  d.el = col;
  if (d.moved) col.classList.add('kanban-column-hidden');
  return true;
}

// Keeps state.colDrag on the current board (relinkColumnDrag()); a drag whose status has no column any
// more ends, unsaved. Whether a column drag is still running.
function syncColumnDrag() {
  if (!state.colDrag) return false;
  if (relinkColumnDrag(document.getElementById('kanban-board'), state.colDrag)) return true;
  endColumnDrag();
  return false;
}

// Ends the column drag without saving: the clone and placeholder go, the dragged column shows again.
function endColumnDrag() {
  stopEdgeScroll();
  const d = state.colDrag;
  if (!d) return;
  state.colDrag = null;
  if (d.clone) d.clone.remove();
  if (d.placeholder) d.placeholder.remove();
  if (d.el) d.el.classList.remove('kanban-column-hidden');
}

function bindColumnDrag() {
  onColMouseDown = (e) => {
    // TPT113 — a column drag now starts from the header handle OR any free space inside
    // the column (header text/dot/count, column body gaps), not just the drag handle.
    // Only reachable while state.editBoardMode is true — bindColumnDrag() is itself only
    // ever called in that mode, so this can't hijack a normal-mode click.
    const col = e.target.closest('.kanban-column');
    if (!col) return;
    if (!e.target.closest('.kanban-column-drag-handle') && !isFreeBoardSpace(e.target)) return;
    const rect = col.getBoundingClientRect();
    state.colDrag = {
      statusId: col.dataset.statusId,
      el: col,
      clone: null,
      placeholder: null,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    e.preventDefault();
  };

  onColMouseMove = (e) => {
    const d = state.colDrag;
    if (!d) return;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!d.moved) {
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
      // Measured from the column on the board now: a re-render since the press replaced the pressed one.
      if (!syncColumnDrag()) return;
      d.moved = true;

      d.clone = d.el.cloneNode(true);
      d.clone.classList.add('kanban-column-dragging');
      d.clone.style.width = d.el.offsetWidth + 'px';
      d.clone.style.height = d.el.offsetHeight + 'px';
      document.body.appendChild(d.clone);

      d.placeholder = document.createElement('div');
      d.placeholder.className = 'kanban-column-placeholder';
      d.placeholder.style.width = d.el.offsetWidth + 'px';
      d.placeholder.style.height = d.el.offsetHeight + 'px';
      d.el.parentNode.insertBefore(d.placeholder, d.el);

      d.el.classList.add('kanban-column-hidden');
    }

    d.clone.style.left = (e.clientX - d.offsetX) + 'px';
    d.clone.style.top = (e.clientY - d.offsetY) + 'px';

    d.lastX = e.clientX;
    d.lastY = e.clientY;
    updateColumnDropTarget(e.clientX, e.clientY);
    if (state.colDrag !== d) return; // its status left the board, which ended the drag

    // TPT113 — auto-scroll the board when the dragged column clone nears its left/right edge.
    const board = document.getElementById('kanban-board');
    if (board) updateEdgeScroll(board, e.clientX, () => updateColumnDropTarget(d.lastX, d.lastY));
  };

  onColMouseUp = async (e) => {
    const d = state.colDrag;
    if (!d) return;
    stopEdgeScroll();
    state.colDrag = null;

    // Must run BEFORE the cleanup below removes 'kanban-column-hidden' from d.el — see
    // columnDropIndex().
    const newIndex = columnDropIndex(document.getElementById('kanban-board'), d.placeholder, d.statusId);

    if (d.clone) d.clone.remove();
    if (d.placeholder) d.placeholder.remove();
    d.el.classList.remove('kanban-column-hidden');

    if (!d.moved || newIndex === -1) return;

    const ordered = [...state.statuses].sort((a, b) => a.display_order - b.display_order);
    const prevIds = ordered.map(s => s.id);
    const from = ordered.findIndex(s => String(s.id) === String(d.statusId));
    if (from === -1) return;
    const [moved] = ordered.splice(from, 1);
    ordered.splice(Math.min(newIndex, ordered.length), 0, moved);
    const ids = ordered.map(s => s.id);
    if (ids.every((sid, i) => sid === prevIds[i])) return; // dropped back in place

    // Optimistic reorder — render the new order immediately, persist after.
    ordered.forEach((s, i) => { s.display_order = i; });
    state.statuses = ordered;
    renderKanban();

    try {
      const res = await api.put(`/projects/${state.projectId}/statuses/reorder`, { ids });
      invalidateStatuses(state.projectId);
      state.statuses = res.statuses || state.statuses;
      if (state.onStatusesChanged) state.onStatusesChanged(state.statuses);
    } catch (err) {
      showToast(err.message || 'Failed to reorder columns', 'error');
      try {
        invalidateStatuses(state.projectId);
        state.statuses = await loadStatuses(state.projectId);
        if (state.onStatusesChanged) state.onStatusesChanged(state.statuses);
      } catch (_) { /* keep optimistic order rather than blank the board */ }
    }
    renderKanban();
  };

  document.addEventListener('mousedown', onColMouseDown);
  document.addEventListener('mousemove', onColMouseMove);
  document.addEventListener('mouseup', onColMouseUp);
}

function cleanupColumnDrag() {
  endColumnDrag();
  if (onColMouseDown) document.removeEventListener('mousedown', onColMouseDown);
  if (onColMouseMove) document.removeEventListener('mousemove', onColMouseMove);
  if (onColMouseUp) document.removeEventListener('mouseup', onColMouseUp);
}

// ── Grab-to-pan (TPT113) ──
// Mouse-down on free board space (not a card, not a native control, and — in Edit Board
// mode — not inside a column, which belongs to the column drag there) pans the board
// horizontally by tracking clientX against scrollLeft. Same house pointer-drag idiom as
// the two drags above, minus the clone/placeholder machinery: panning has no drop step,
// just a live scrollLeft write.

let onPanMouseDown, onPanMouseMove, onPanMouseUp;

function bindBoardPan() {
  const board = document.getElementById('kanban-board');
  if (board) board.classList.add('kanban-board-pannable');

  onPanMouseDown = (e) => {
    if (e.button !== 0) return;
    const board = e.target.closest('.kanban-board');
    if (!board) return;
    if (state.drag || state.colDrag) return; // a card/column drag already claimed this gesture
    if (!isFreeBoardSpace(e.target)) return;
    if (e.target.closest('.kanban-column-drag-handle')) return;
    // Edit Board mode: free space *inside* a column starts a column reorder drag instead —
    // pan only claims space outside every column (board gutter, right of the last column).
    if (state.editBoardMode && e.target.closest('.kanban-column')) return;

    state.pan = {
      board,
      startX: e.clientX,
      startScrollLeft: board.scrollLeft,
      moved: false,
    };
    e.preventDefault();
  };

  onPanMouseMove = (e) => {
    const p = state.pan;
    if (!p) return;
    const dx = e.clientX - p.startX;
    if (!p.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      p.moved = true;
      p.board.classList.add('kanban-board-panning');
    }
    p.board.scrollLeft = p.startScrollLeft - dx;
  };

  onPanMouseUp = () => {
    const p = state.pan;
    if (!p) return;
    p.board.classList.remove('kanban-board-panning');
    state.pan = null;
  };

  document.addEventListener('mousedown', onPanMouseDown);
  document.addEventListener('mousemove', onPanMouseMove);
  document.addEventListener('mouseup', onPanMouseUp);
}

function cleanupBoardPan() {
  if (state.pan) {
    state.pan.board.classList.remove('kanban-board-panning');
    state.pan = null;
  }
  const board = document.getElementById('kanban-board');
  if (board) board.classList.remove('kanban-board-pannable');
  if (onPanMouseDown) document.removeEventListener('mousedown', onPanMouseDown);
  if (onPanMouseMove) document.removeEventListener('mousemove', onPanMouseMove);
  if (onPanMouseUp) document.removeEventListener('mouseup', onPanMouseUp);
}

// "+ Add item" opens the board owner's create modal with the column's status selected.
function bindAddItem(container) {
  const onClick = (e) => {
    const button = e.target.closest('.kanban-add-item');
    if (!button) return;
    if (state.onAddTask) state.onAddTask(button.dataset.status);
  };
  container.addEventListener('click', onClick);
  return () => container.removeEventListener('click', onClick);
}

// ── Sub-view render (used by board container) ──

export function renderKanbanView(container, tasks, projectId, members, statuses, opts = {}) {
  resetState();
  state.projectId = projectId;
  state.tasks = tasks;
  state.members = members || [];
  state.statuses = statuses || [];
  state.editBoardMode = !!opts.editBoardMode;
  state.onStatusesChanged = opts.onStatusesChanged || null;
  state.onDataChanged = opts.onDataChanged || null;
  state.sprintRows = opts.sprintRows || null;
  state.onAddTask = opts.onAddTask || null;
  state.canWrite = opts.canWrite !== false;
  state.projectPermissionMode = opts.projectPermissionMode ?? null;
  state.language = opts.language ?? 'en';
  // (TPT9099) Selecting cards is a write affordance: without task:write there is no checkbox.
  state.selection = state.canWrite ? (opts.selection || null) : null;

  container.innerHTML = `<div id="kanban-board" class="kanban-board${state.canWrite ? '' : ' board-readonly'}"></div>`;
  renderKanban();
  const board = document.getElementById('kanban-board');
  // kanban-board--edit (TPT113) styles the whole column as a drag target in Edit Board mode.
  board?.classList.toggle('kanban-board--edit', state.editBoardMode);
  bindDragAndDrop();
  if (state.editBoardMode) bindColumnDrag();
  bindBoardPan();
  const unbindAddItem = bindAddItem(container);
  const unbindHoverExpand = bindHoverExpand(board);
  // A column's cards are its group: Shift-click ranges stay inside one column body.
  state.cardSelect = state.selection ? bindCardSelection(board, {
    selection: state.selection,
    groupBodySelector: '.kanban-column-body',
    groupKeys: status => state.tasks.filter(t => t.status === status).map(t => t.task_key),
  }) : null;
  return () => {
    unbindAddItem();
    // Only what this view opened (TPT647). Modals are not ours: board.js owns the create/import/
    // sprint modals and the drawer owns its own confirms.
    removeAssigneePicker(state.pickerToken);
    closeTaskDetail(state.detailToken);
    cleanupDrag();
    cleanupColumnDrag();
    cleanupBoardPan();
    unbindHoverExpand();
    // The selection itself is board.js's and stays; only this mount's listeners go.
    if (state.cardSelect) state.cardSelect.unbind();
    resetState();
  };
}
